import { useState, useRef, useEffect } from "react";
import { TerminalSquare, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";

export default function CloudShell({ onClose }) {
  const { refresh, persona } = useAzure();
  const [lines, setLines] = useState([
    { t: "out", v: "Azure Lab Cloud Shell (training). Type 'help' to see supported commands." },
  ]);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [hIdx, setHIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [lines]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async () => {
    const cmd = input.trim();
    if (!cmd || busy) return;
    setInput("");
    setHistory((h) => [...h, cmd]);
    setHIdx(-1);
    setLines((l) => [...l, { t: "in", v: cmd }]);
    setBusy(true);
    try {
      const { data } = await api.post("/azure/cloudshell", { command: cmd });
      if (data.output === "__CLEAR__") {
        setLines([]);
      } else {
        setLines((l) => [...l, { t: "out", v: data.output }]);
      }
      await refresh();
    } catch (e) {
      setLines((l) => [...l, { t: "err", v: "Shell error. Try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") submit();
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!history.length) return;
      const ni = hIdx < 0 ? history.length - 1 : Math.max(0, hIdx - 1);
      setHIdx(ni); setInput(history[ni]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (hIdx < 0) return;
      const ni = hIdx + 1;
      if (ni >= history.length) { setHIdx(-1); setInput(""); } else { setHIdx(ni); setInput(history[ni]); }
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 h-72 z-30 bg-[#0b0b0c] border-t-2 border-[#0078d4] flex flex-col" data-testid="az-cloudshell">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 text-white/80">
        <TerminalSquare className="h-4 w-4 text-[#3b9eff]" />
        <span className="text-sm font-medium">Cloud Shell</span>
        <span className="text-xs text-white/40 font-mono">{persona?.upn}</span>
        <Button size="icon" variant="ghost" className="ml-auto h-7 w-7 text-white/70 hover:text-white hover:bg-white/10" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 font-mono text-[13px] leading-relaxed" onClick={() => inputRef.current?.focus()}>
        {lines.map((l, i) => (
          <div key={i} className={l.t === "in" ? "text-[#3b9eff]" : l.t === "err" ? "text-red-400" : "text-emerald-100/90"}>
            {l.t === "in" ? <><span className="text-white/40">azure@sandbox:~$ </span>{l.v}</> : <pre className="whitespace-pre-wrap font-mono">{l.v}</pre>}
          </div>
        ))}
        <div className="flex items-center text-[#3b9eff]">
          <span className="text-white/40">azure@sandbox:~$&nbsp;</span>
          <input
            ref={inputRef}
            data-testid="az-cloudshell-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={busy}
            className="flex-1 bg-transparent outline-none text-white caret-[#3b9eff]"
            spellCheck={false} autoComplete="off"
          />
        </div>
        <div ref={endRef} />
      </div>
    </div>
  );
}
